import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { setCurrentStep } from '../../store/slices/sellerProfileSlice';
import { message, Progress } from 'antd';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

const BUSINESS_PROFILE_CREATION_STEPS = [
  { title: 'components.companyInfo', description: 'components.companyInfoDesc', percentage: 35 },
  { title: 'components.catalogue', description: 'components.catalogueDesc', percentage: 15 },
  { title: 'components.category', description: 'components.categoryDesc', percentage: 20 },
  { title: 'components.awards', description: 'components.awardsDesc', percentage: 15, canSkip: true },
  { title: 'components.branches', description: 'components.branchesDesc', percentage: 15, canSkip: true },
  { title: 'components.review', description: 'components.reviewDesc', percentage: 0 },
];

const steps = BUSINESS_PROFILE_CREATION_STEPS.filter((_, index) => index < 5);

const ProfileCreationStepper = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentStep } = useSelector((state: RootState) => state.sellerProfile);
  const isMediumAndDown = window.innerWidth <= 768;

  const filteredSteps = isMediumAndDown
    ? steps?.filter((_, idx) => currentStep === idx)
    : steps;

  const handleStepChange = (val: number) => {
    if (val > currentStep && currentStep < 3) {
      message.error(t('components.finishCurrentStep'));
    } else {
      dispatch(setCurrentStep(val));
    }
  };

  const currentStepData = steps[currentStep] || steps[0];
  const progressPercent = Math.round(((currentStep + 1) / steps.length) * 100);

  const COLORS = {
    success: '#4DA167',
    active: '#1890ff',
    upcoming: '#8c8c8c',
    description: '#8c8c8c',
    dottedLine: '#8c8c8c',
  };

  return (
    <div
      data-testid="profile-creation-stepper-container"
      className="stepper-container h-full dark:bg-soft-dark-background pb-0 flex flex-col justify-between items-center rounded-xl"
    >
      {isMediumAndDown ? (
        <div className="w-full p-4" data-testid="mobile-stepper-indicator">
          <div className="flex justify-between items-center mb-2">
            <p className="text-lg font-semibold dark:text-dark-text-primary" data-testid="mobile-step-title">
              {t(currentStepData.title)}
            </p>
            <p className="text-sm text-text-secondary dark:text-dark-text-secondary" data-testid="mobile-step-count">
              {t('components.stepIndicator', { current: currentStep + 1, total: steps.length })}
            </p>
          </div>
          <Progress
            percent={progressPercent}
            showInfo={false}
            strokeColor={COLORS.active}
            trailColor="rgba(0, 0, 0, 0.06)"
            size="small"
            data-testid="mobile-step-progress"
          />
        </div>
      ) : (
        <div className="p-6 pt-16 h-full w-full flex flex-col" data-testid="profile-creation-stepper">
          {filteredSteps?.map((step, index) => {
            const actualIndex = steps.indexOf(step);
            const isFinished = actualIndex < currentStep;
            const isActive = actualIndex === currentStep;
            const isLast = index === filteredSteps.length - 1;

            const currentStateColor = isFinished
              ? COLORS.success
              : isActive
                ? COLORS.active
                : COLORS.upcoming;

            return (
              <div
                key={actualIndex}
                className="flex flex-row cursor-pointer"
                onClick={() => handleStepChange(actualIndex)}
                data-testid={`profile-creation-step-${actualIndex}`}
              >
                <div className="flex flex-col items-center me-4 flex-shrink-0 relative" style={{ width: 18 }}>
                  {actualIndex === 0 && (
                    <div
                      className="absolute -top-6 w-0 border-l-[1px] border-dotted h-6 z-0"
                      style={{ borderColor: COLORS.dottedLine }}
                    />
                  )}
                  <div
                    className="rounded-full flex-shrink-0 mt-[4px] flex items-center justify-center bg-white dark:bg-dark-surface-primary z-10 border-[1.5px]"
                    style={{ width: 18, height: 18, borderColor: currentStateColor }}
                  >
                    <div
                      className="rounded-full"
                      style={{ width: 8, height: 8, backgroundColor: currentStateColor }}
                    />
                  </div>
                  {!isLast && (
                    <div
                      className="flex-1 w-0 border-l-[1px] border-dotted my-1 z-0"
                      style={{ minHeight: 56, borderColor: COLORS.dottedLine }}
                    />
                  )}

                </div>
                <div className={cn('flex flex-col', !isLast ? 'pb-8' : 'pb-0')} data-testid={`profile-creation-step-${actualIndex}-content`}>
                  <p
                    className="text-[18px] leading-tight"
                    style={{ color: currentStateColor, fontWeight: 700 }}
                    data-testid={`profile-creation-step-${actualIndex}-title`}
                  >
                    {t(step?.title)}{' '}
                    {step.canSkip && (
                      <span className="font-normal opacity-80" data-testid={`profile-creation-step-${actualIndex}-skip-label`}>
                        {t('components.canSkip')}
                      </span>
                    )}
                  </p>
                  <p
                    className="text-[14px] font-normal mt-[2px] "
                    style={{ color: COLORS.description, lineHeight: '1.4' }}
                    data-testid={`profile-creation-step-${actualIndex}-description`}
                  >
                    {t(step?.description)}
                    {/* <span className="inline-block">({step?.percentage}%)</span> */}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProfileCreationStepper;
